import React from 'react';
import { Box, Typography, Avatar } from '@mui/material';
import { SmartToy as BotIcon } from '@mui/icons-material';

interface ChatContainerProps {
  children: React.ReactNode;
}

interface ChatHeaderProps {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}

interface ChatMessagesProps {
  children: React.ReactNode;
}

interface ChatInputProps {
  children: React.ReactNode;
}

const ChatContainer: React.FC<ChatContainerProps> = ({ children }) => {
  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden',
      bgcolor: 'background.paper',
      borderRadius: 2,
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
      border: '1px solid',
      borderColor: 'divider',
    }}>
      {children}
    </Box>
  );
};

const ChatHeader: React.FC<ChatHeaderProps> = ({ title, subtitle, actions }) => {
  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      p: 3,
      bgcolor: 'primary.main',
      color: 'primary.contrastText',
      borderRadius: '8px 8px 0 0',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
          <BotIcon />
        </Avatar>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'inherit' }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ opacity: 0.9, color: 'inherit' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {actions && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          {actions}
        </Box>
      )}
    </Box>
  );
};

const ChatMessages: React.FC<ChatMessagesProps> = ({ children }) => {
  return (
    <Box sx={{
      flex: 1,
      overflow: 'auto',
      p: 2,
      bgcolor: 'background.default',
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
    }}>
      {children}
    </Box>
  );
};

const ChatInput: React.FC<ChatInputProps> = ({ children }) => {
  return (
    <Box sx={{
      borderTop: 1,
      borderColor: 'divider',
      bgcolor: 'background.paper',
      p: 1.5,
      borderRadius: '0 0 8px 8px',
    }}>
      {children}
    </Box>
  );
};

// Compound component pattern with proper typing
interface ChatContainerComponent extends React.FC<ChatContainerProps> {
  Header: React.FC<ChatHeaderProps>;
  Messages: React.FC<ChatMessagesProps>;
  Input: React.FC<ChatInputProps>;
}

// Attach sub-components
(ChatContainer as ChatContainerComponent).Header = ChatHeader;
(ChatContainer as ChatContainerComponent).Messages = ChatMessages;
(ChatContainer as ChatContainerComponent).Input = ChatInput;

export default ChatContainer as ChatContainerComponent;